const SESSION_STORAGE_KEY =
  'retzia_session_token';

const COURSE_ACTIVITY_LAUNCH_STORAGE_KEY =
  'retzia_course_activity_launch';

let memoryToken: string | null = null;
let memoryCourseActivityLaunch: string | null = null;

const isValidToken = (
  value: string | null
): value is string =>
  typeof value === 'string' &&
  /^[A-Za-z0-9_-]{40,120}$/.test(value);

const readSessionStorageToken = (
  key: string
): string | null => {
  try {
    const stored =
      window.sessionStorage.getItem(key);

    return isValidToken(stored)
      ? stored
      : null;
  } catch {
    return null;
  }
};

const storeSessionStorageToken = (
  key: string,
  value: string
): void => {
  try {
    window.sessionStorage.setItem(
      key,
      value
    );
  } catch {
    // Safari puede bloquear el almacenamiento
    // en algunos contextos incrustados.
  }
};

export const getSessionToken = (): string | null => {
  if (isValidToken(memoryToken)) {
    return memoryToken;
  }

  const stored =
    readSessionStorageToken(
      SESSION_STORAGE_KEY
    );

  if (stored) {
    memoryToken = stored;
    return stored;
  }

  return null;
};

export const getCourseActivityLaunchToken =
  (): string | null => {
    if (isValidToken(memoryCourseActivityLaunch)) {
      return memoryCourseActivityLaunch;
    }

    const stored =
      readSessionStorageToken(
        COURSE_ACTIVITY_LAUNCH_STORAGE_KEY
      );

    if (stored) {
      memoryCourseActivityLaunch = stored;
      return stored;
    }

    return null;
  };

export const captureSessionTokenFromHash =
  (): string | null => {
    const rawHash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;

    if (rawHash !== '') {
      const parameters =
        new URLSearchParams(rawHash);

      const sessionCandidate =
        parameters.get('session_token');

      if (isValidToken(sessionCandidate)) {
        memoryToken = sessionCandidate;

        storeSessionStorageToken(
          SESSION_STORAGE_KEY,
          sessionCandidate
        );
      }

      const courseActivityCandidate =
        parameters.get('course_activity_launch');

      if (isValidToken(courseActivityCandidate)) {
        memoryCourseActivityLaunch =
          courseActivityCandidate;

        storeSessionStorageToken(
          COURSE_ACTIVITY_LAUNCH_STORAGE_KEY,
          courseActivityCandidate
        );
      }

      if (
        (
          parameters.has('session_token') ||
          parameters.has('sso')
        ) &&
        window.self === window.top
      ) {
        const cleanUrl =
          window.location.pathname +
          window.location.search;

        try {
          window.history.replaceState(
            window.history.state,
            document.title,
            cleanUrl
          );
        } catch {
          try {
            window.history.replaceState(
              null,
              document.title,
              cleanUrl
            );
          } catch {
            // La autenticación debe continuar aunque
            // el navegador no permita limpiar la URL.
          }
        }
      }
    }

    return getSessionToken();
  };

export const clearCourseActivityLaunchToken =
  (): void => {
    memoryCourseActivityLaunch = null;

    try {
      window.sessionStorage.removeItem(
        COURSE_ACTIVITY_LAUNCH_STORAGE_KEY
      );
    } catch {
      // Puede estar bloqueado en algunos iframes.
    }

    const rawHash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;

    if (rawHash === '') {
      return;
    }

    const parameters =
      new URLSearchParams(rawHash);

    if (!parameters.has('course_activity_launch')) {
      return;
    }

    parameters.delete('course_activity_launch');

    const remainingHash =
      parameters.toString();

    const cleanUrl =
      window.location.pathname +
      window.location.search +
      (
        remainingHash !== ''
          ? `#${remainingHash}`
          : ''
      );

    try {
      window.history.replaceState(
        window.history.state,
        document.title,
        cleanUrl
      );
    } catch {
      // No es crítico: el launch ya fue consumido
      // de forma segura en el backend.
    }
  };

export const getSessionAuthorizationHeaders =
  (): Record<string, string> => {
    const token = getSessionToken();

    return token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {};
  };
